import { createContext, useContext, useState, useEffect } from "react";
import { FadeInTransition } from "@/utils/animation";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  Grid,
  Heading,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  ButtonGroup,
  Collapse,
  Text,
  Tooltip,
  Spacer,
  useToken,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { AnimatePresence } from "framer-motion";
import { useRouter } from "next/router";
import { AddIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Select } from "chakra-react-select";
import { useUserData } from "@/contexts/UserDataContext";
import { useAPI } from "@/contexts/APIContext";
import { HSCreateAccount } from "../user/CreateAccount";
import { ACADEMIC_INTEREST_TOP } from "@/utils/profile";
import { hsUserAnalytics } from "@/utils/analytics";
import { createValueList } from "@/utils/ui/select-helper";
import { EmojiBox } from "@/components/shared/generic";
import { countryOptions } from "@/utils/data/locations";

export const UserInfoContext = createContext();

export const UserInfoProvider = ({ pageName, children, bordered, nQuestions }) => {
  const router = useRouter();
  const { userData } = useUserData();
  const [userInfo, setUserInfo] = useState(userData || {});
  const [questionId, setQuestionId] = useState(0);

  const setKey = (key, value) => {
    setUserInfo((userInfo) => ({ ...userInfo, [key]: value }));
  };

  const onClickNext = (newQuestionId) => {
    hsUserAnalytics.onAddInfo(pageName, questionId);

    if (newQuestionId !== undefined && newQuestionId !== null) {
      if (newQuestionId < 0) {
        newQuestionId += nQuestions;
      }
      setQuestionId(newQuestionId);
    } else {
      setQuestionId((i) => i + 1);
    }
  };

  const { askAdditionalQuestions, skipCompleted } = router.query;
  const shouldAskAdditionalQuestions = askAdditionalQuestions === "true";
  const shouldSkipCompletedQuestions = skipCompleted === "true";

  const registerSkippableQuestion = (key) => {
    if (key in userInfo && shouldSkipCompletedQuestions) {
      onClickNext();
    }
  };

  useEffect(() => {
    if (userInfo && userInfo.affiliateCode) {
      return;
    }

    // Get from param
    const affiliateCodeParam = router.query.affiliateCode;
    if (affiliateCodeParam) {
      return setKey("affiliateCode", affiliateCodeParam);
    }

    // Get from local storage
    if (typeof window !== "undefined") {
      const affiliateCodeLocal = window.localStorage.getItem("affiliateCode");
      if (affiliateCodeLocal) {
        return setKey("affiliateCode", affiliateCodeLocal);
      }
    }
  }, [router]);

  return (
    <UserInfoContext.Provider
      value={{
        userInfo,
        bordered,
        nQuestions,
        setKey,
        questionId,
        onClickNext,
        shouldAskAdditionalQuestions,
        shouldSkipCompletedQuestions,
        registerSkippableQuestion,
      }}
    >
      {children}
    </UserInfoContext.Provider>
  );
};

export const useUserInfo = () => useContext(UserInfoContext);

export const Question = ({ title, children, shadowColor = "gray.300" }) => {
  const { questionId, nQuestions, bordered } = useUserInfo();
  const realShadowColor = useToken("colors", shadowColor);
  const containerProps = bordered
    ? {
        boxShadow: "-0.3rem 0.3rem " + realShadowColor,
        border: "1px solid black",
        borderRadius: "xl",
      }
    : {};
  return (
    <FadeInTransition withYMovement>
      <Container
        key={0}
        p={6}
        textAlign="center"
        bg="white"
        maxW="container.sm"
        {...containerProps}
      >
        {questionId !== 0 && questionId !== nQuestions && (
          <Text fontFamily="monospace" textAlign="center" textTransform="uppercase" mb={8}>
            Question <b>{questionId + 1}</b> of <b>{nQuestions}</b>
          </Text>
        )}
        {title && (
          <Heading as="h1" fontSize="3xl">
            {title}
          </Heading>
        )}
        <Flex justify="center" flexDir="column" mt={4} gap={{ md: 10 }}>
          {children}
        </Flex>
      </Container>
    </FadeInTransition>
  );
};

export const QuestionUserType = ({ skippable = true }) => {
  const { setKey, onClickNext, registerSkippableQuestion } = useUserInfo();

  const onClick = (consumerType) => {
    setKey("consumerType", consumerType);
    setKey("type", "hs");
    onClickNext();
    hsUserAnalytics.onSetConsumerType(consumerType);
  };

  useEffect(() => {
    if (skippable) {
      registerSkippableQuestion("consumerType");
    }
  }, []);

  return (
    <Question title="What best describes you?">
      <Flex direction="row" justify="center" gap={10} align="center">
        <EmojiBox
          emoji="👩‍👧"
          label="I'm a parent."
          color="red.300"
          onClick={() => onClick("parent")}
        />
        <EmojiBox
          emoji="🧑‍🎓"
          label="I'm a student."
          color="gray.300"
          onClick={() => onClick("student")}
        />
      </Flex>
    </Question>
  );
};

export const NextButton = ({ onClick, children }) => {
  const { onClickNext } = useUserInfo();

  const onButtonClick = () => {
    if (onClick) {
      onClick();
    }
    onClickNext();
  };

  return (
    <Flex w="100%">
      <Spacer />
      <Button
        type={"submit"}
        onClick={onButtonClick}
        mt={4}
        variant="solid"
        colorScheme="red"
        rightIcon={<ChevronRightIcon />}
      >
        {children ?? "Next"}
      </Button>
    </Flex>
  );
};

export const GridMultiSelector = ({ children }) => {
  return (
    <>
      <Flex
        display={{ base: "none", md: "flex" }}
        wrap="wrap"
        w="100%"
        justify="space-between"
        gap={2}
        mt={10}
      >
        {children}
      </Flex>
      <Grid display={{ base: "grid", md: "none" }} templateColumns="repeat(2, 1fr)" gap={2} mt={10}>
        {children}
      </Grid>
    </>
  );
};

export const CurrentQuestionRenderer = ({ components, bg, backdropFilter }) => {
  const { questionId } = useUserInfo();
  const Component = components[questionId];

  return (
    <AnimatePresence mode="wait">
      <Box zIndex={1} key={questionId} bg={bg} backdropFilter={backdropFilter}>
        <Component />
      </Box>
    </AnimatePresence>
  );
};

export const QuestionTargetColleges = ({ title, subtitle, options, shortcuts }) => {
  const { setKey, registerSkippableQuestion, userInfo } = useUserInfo();
  const [schools, setSchools] = useState(createValueList(userInfo.targetColleges || [], options));

  const addSchools = (colleges) => {
    setSchools((schools) => [...new Set([...schools, ...createValueList(colleges, options)])]);
  };

  useEffect(
    () =>
      setKey(
        "targetColleges",
        schools.map((college) => college.value),
      ),
    [schools],
  );

  useEffect(() => {
    registerSkippableQuestion("targetColleges");
  }, []);

  return (
    <Question title={title}>
      <Box>
        <Text fontSize="xl" mb={10}>
          {subtitle}
        </Text>
        <Select
          isMulti
          placeholder="Search for a school..."
          variant="flushed"
          options={options}
          value={schools}
          onChange={setSchools}
        />
        <ButtonGroup display="block">
          {shortcuts.map(({ name, colleges }, i) => (
            <Button
              mt={2}
              leftIcon={<AddIcon />}
              size="md"
              onClick={() => addSchools(colleges)}
              key={i}
            >
              {name}
            </Button>
          ))}
        </ButtonGroup>
      </Box>
      <NextButton />
    </Question>
  );
};

export const QuestionAcademicInterests = ({
  maxChoices,
  title,
  userKey = "academicInterests",
  // allowUndecided,
}) => {
  const { setKey, registerSkippableQuestion, userInfo } = useUserInfo();
  const [selected, setSelected] = useState(userInfo[userKey] || []);
  const importantInterests = ACADEMIC_INTEREST_TOP;
  const setItemSelected = (interest, isSelected) => {
    if (isSelected) {
      setSelected([...selected, interest]);
    } else {
      setSelected(selected.filter((i) => i !== interest));
    }
  };

  useEffect(() => {
    if (selected.length > maxChoices) {
      if (maxChoices === 1) {
        setSelected([selected[selected.length - 1]]);
      } else {
        setSelected(selected.slice(0, maxChoices));
      }
    } else {
      setKey(userKey, selected);
    }
  }, [selected]);

  useEffect(() => {
    registerSkippableQuestion(userKey);
  }, []);

  return (
    <Question title={title}>
      <Box>
        {maxChoices > 1 && (
          <Text textAlign="center" fontSize="lg">
            Select up to {maxChoices}.
          </Text>
        )}
        <GridMultiSelector>
          {importantInterests.map((interest) => {
            const isSelected = selected.includes(interest);
            return (
              <Button
                px={7}
                key={interest}
                onClick={() => setItemSelected(interest, !isSelected)}
                variant={isSelected ? "solid" : "outline"}
                size="md"
                colorScheme={isSelected ? "red" : "gray"}
              >
                {interest}
              </Button>
            );
          })}
        </GridMultiSelector>
        <NextButton />
      </Box>
    </Question>
  );
};

export const AdmissionsYearSelector = ({ title, userKey = "admissionsYear" }) => {
  const { setKey, userInfo } = useUserInfo();
  const [year, setYear] = useState(userInfo[userKey]);

  useEffect(() => {
    setKey(userKey, year);
  }, [year]);

  // useEffect(() => {
  //   registerSkippableQuestion(userKey);
  // }, []);

  return (
    <Question title={title}>
      <Box>
        <Text textAlign="center" fontSize="lg">
          Input your graduation / admissions year.
        </Text>

        <NumberInput
          defaultValue={year}
          min={new Date().getFullYear()}
          max={3000}
          maxLength={4}
          onChange={(valueString) => setYear(parseInt(valueString))}
          keepWithinRange={true}
          clampValueOnBlur={false}
          sx={{ width: "100%", maxWidth: "300px", margin: "0 auto" }}
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>

        <NextButton />
      </Box>
    </Question>
  );
};

export const GeographicalSelector = ({ title, userKey = "geographicalLocation" }) => {
  const { setKey, userInfo } = useUserInfo();
  const [country, setCountry] = useState(userInfo[userKey] || "");

  useEffect(() => {
    setKey(userKey, country);
  }, [country]);

  return (
    <Question title={title}>
      <Box>
        <Text textAlign="center" fontSize="lg">
          Input the country you currently stay in.
        </Text>

        <Select
          placeholder="Select country"
          options={countryOptions}
          onChange={(option) => {
            setCountry(option?.value || "");
          }}
        />

        <NextButton />
      </Box>
    </Question>
  );
};

export const QuestionTestScores = () => {
  const { setKey, userInfo, registerSkippableQuestion } = useUserInfo();
  const [showSAT, setShowSAT] = useState(!!userInfo.satScore);
  const [showACT, setShowACT] = useState(!!userInfo.actScore);
  const [actScore, setACTScore] = useState(userInfo.actScore ?? 36);
  const [satScore, setSATScore] = useState(userInfo.satScore ?? 1600);

  useEffect(() => setKey("satScore", showSAT ? satScore : null), [satScore, showSAT]);
  useEffect(() => setKey("actScore", showACT ? actScore : null), [actScore, showACT]);

  useEffect(() => {
    registerSkippableQuestion("satScore");
    registerSkippableQuestion("actScore");
  }, []);

  return (
    <Question
      title={`📝 What are ${
        userInfo.consumerType === "parent" ? "your child's" : "your"
      } test scores?`}
    >
      <Box mt={8}>
        <Checkbox
          mb={2}
          colorScheme="red"
          checked={showSAT}
          onChange={(e) => setShowSAT(e.target.checked)}
        >
          I&apos;ve taken the SAT
        </Checkbox>
        <Collapse in={showSAT} style={{ overflow: "visible" }}>
          <Text mb={2}>What was your SAT score?</Text>
          <Slider
            value={satScore}
            onChange={setSATScore}
            min={800}
            max={1600}
            step={10}
            colorScheme="red"
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            {showSAT && (
              <Tooltip
                hasArrow
                bg="red.500"
                color="white"
                placement="bottom"
                isOpen
                label={satScore}
              >
                <SliderThumb />
              </Tooltip>
            )}
          </Slider>
        </Collapse>
      </Box>
      <Box>
        <Checkbox
          mb={2}
          colorScheme="red"
          checked={showACT}
          onChange={(e) => setShowACT(e.target.checked)}
        >
          I&apos;ve taken the ACT
        </Checkbox>
        <Collapse in={showACT} style={{ overflow: "visible" }}>
          <Text mb={2}>What was your ACT score?</Text>
          <Slider
            value={actScore}
            onChange={setACTScore}
            min={1}
            max={36}
            step={1}
            colorScheme="red"
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            {showACT && (
              <Tooltip
                hasArrow
                bg="red.500"
                color="white"
                placement="bottom"
                isOpen
                label={actScore}
              >
                <SliderThumb />
              </Tooltip>
            )}
          </Slider>
        </Collapse>
      </Box>
      <NextButton />
    </Question>
  );
};

export const QuestionSetupAccount = ({ redirectURL = "/profiles" }) => {
  const { userInfo } = useUserInfo();
  const { currentUser } = useUserData();
  const api = useAPI();
  const router = useRouter();
  const [savingToProfile, setSavingToProfile] = useState(false);

  const onComplete = async () => {
    setTimeout(async () => {
      await api.post("users/data", true, userInfo);
      await router.push(redirectURL);
    }, 500);
  };

  useEffect(() => {
    if (currentUser) {
      setSavingToProfile(true);
      onComplete();
    }
  }, []);

  return (
    <Question title="Almost done!">
      {savingToProfile ? (
        <Text fontSize="2xl">Redirecting&hellip;</Text>
      ) : (
        <>
          <Text fontSize="2xl">To finish, create an account with us.</Text>
          <HSCreateAccount onComplete={onComplete} />
        </>
      )}
    </Question>
  );
};
